import styled from '@emotion/styled';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
    PageContentHorizontalAlignment,
    PageContentVerticalAlignment,
} from 'src/generated/webapp_gql';
import { Button } from 'src/shared/components/Button';

export interface VerticalAlignmentProps {
    contentVerticalAlignment?: PageContentVerticalAlignment;
}
export interface HorizontalAlignmentProps {
    contentHorizontalAlignment: PageContentHorizontalAlignment;
}

export interface FlaggedAlignedContentProps {
    alignContent: boolean;
}

const Container = styled.div({
    marginTop: '10vh',
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    padding: '0 16px',
    flexDirection: 'column',
});

const Content = styled.div({
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    flexDirection: 'column',
});

const PageContainer = styled.div<VerticalAlignmentProps>(
    {
        marginTop: 30,
        display: 'flex',
        flex: 1,
        alignItems: 'center',
        padding: '0 16px',
        flexDirection: 'column',
    },
    ({ contentVerticalAlignment }) => {
        switch (contentVerticalAlignment) {
            case PageContentVerticalAlignment.Middle:
                return {
                    justifyContent: 'center',
                };
            case PageContentVerticalAlignment.Top:
            default:
                return {
                    justifyContent: 'flex-start',
                };
        }
    },
);

const NavigationContainer = styled.div<{ justifyNavigation: PageProps['justifyNavigation'] }>(
    ({ justifyNavigation }) => ({
        display: 'flex',
        flexDirection: 'row',
        justifyContent: justifyNavigation,
        marginTop: 40,
    }),
);

const NavigationButton = styled(Button)({
    '&&': {
        minWidth: 241,
        maxWidth: 241,
    },
});

export interface PageProps extends VerticalAlignmentProps {
    onNavigateNext: VoidFunction;
    onNavigatePrev: VoidFunction;
    justifyNavigation?: 'flex-start' | 'center';
}

const ResolvedContainer: React.FC<VerticalAlignmentProps> = ({
    children,
    contentVerticalAlignment,
}) => {
    const { revampedProgramsOnWeb } = useFlags();

    return revampedProgramsOnWeb ? (
        <PageContainer contentVerticalAlignment={contentVerticalAlignment}>
            <Content>{children}</Content>
        </PageContainer>
    ) : (
        <Container>
            <Content>{children}</Content>
        </Container>
    );
};

export const Page: FC<PageProps> = ({
    onNavigateNext,
    onNavigatePrev,
    children,
    justifyNavigation = 'flex-start',
    contentVerticalAlignment = PageContentVerticalAlignment.Middle,
}) => {
    const { t } = useTranslation();

    return (
        <ResolvedContainer contentVerticalAlignment={contentVerticalAlignment}>
            {children}
            <NavigationContainer justifyNavigation={justifyNavigation}>
                <NavigationButton type="white" onClick={onNavigatePrev}>
                    {t('common.back')}
                </NavigationButton>
                <NavigationButton type="accept" onClick={onNavigateNext} marginLeft={16}>
                    {t('common.continue')}
                </NavigationButton>
            </NavigationContainer>
        </ResolvedContainer>
    );
};
